import { effect, inject, Injectable, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import deLocale from 'primelocale/de.json';
import { Translation } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { ContentService } from 'lion-api';
import { AppConfigService } from '@shared/data';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { Base64 } from 'js-base64';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private contentService = inject(ContentService);
  private translateService = inject(TranslateService);
  private appConfigService = inject(AppConfigService);
  private primeNg = inject(PrimeNG);

  constructor() {
    effect(() => {
      const publicConfig = this.appConfigService.publicConfig();
      untracked(() => {
        if (publicConfig) {
          this.loadTranslation('de', '/client/i18n/de.json');
          this.loadTranslation('en', '/client/i18n/en.json');
        }
      });
    });
  }

  loadAndSetTranslationsAsync() {
    this.setInitialTranslation();
  }

  private loadTranslation(lang: string, path: string) {
    this.contentService
      .contentManagementDownloadFileGet(path)
      .pipe(
        switchMap((blob) => from(blob.text())),
        map((langText) => {
          try {
            return JSON.parse(langText);
          } catch {
            console.info('Json is probably base64 encoded. Trying to decode it.');
            const utf8String = Base64.decode(langText);
            return JSON.parse(utf8String);
          }
        }),
        catchError((err) => {
          console.log('Failed to load translation:', err.message);
          return of(undefined);
        })
      )
      .subscribe((langJson) => {
        if (langJson) {
          this.translateService.setTranslation(lang, langJson);
        }
      });
  }

  private setInitialTranslation() {
    this.translateService.addLangs(['de', 'en']);
    this.translateService.setFallbackLang('de');
    this.translateService.use('de');
    this.primeNg.setTranslation(deLocale.de as Translation);
  }
}
